import './App.css';
import React, { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { createDNAHelix } from './models/passionDataAnalysisModel.js';
import { createNetworkModel } from './models/passionMachineLearningModel.js';
import { createCodingModel } from './models/passionCodingModel.js';

import ProjectTile from './components/ProjectTile';
import projects, { passions } from './data/projects';

gsap.registerPlugin(ScrollTrigger);

function App() {
  useEffect(() => {
    const isFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    // Cursor state
    let mouseX = 0, mouseY = 0;
    let currentX = 0, currentY = 0;
    const delay = 0.1;

    // Get canvases
    const dnaCanvas = document.getElementById('dna-model');
    const mlCanvas = document.getElementById('ml-model');
    const codingCanvas = document.getElementById('coding-model');

    // Initialize models and store controllers
    const controllers = {};

    if (!dnaCanvas.dataset.initialized) {
      controllers.data = createDNAHelix(dnaCanvas, 0x574371, 0xff4ecd);
      dnaCanvas.dataset.initialized = "true";
    }

    if (!mlCanvas.dataset.initialized) {
      controllers.ml = createNetworkModel(mlCanvas, 0x574371, 0xff4ecd);
      mlCanvas.dataset.initialized = "true";
    }

    if (!codingCanvas.dataset.initialized) {
      controllers.coding = createCodingModel(codingCanvas, 0x574371, 0xff4ecd);
      codingCanvas.dataset.initialized = "true";
    }

    // Consolidated animation loop
    let animationFrameId = null;
    const animate = () => {
      // Update cursor position
      if (isFinePointer) {
        const cursor = document.getElementById('custom-cursor');
        const follow = document.getElementById('custom-cursor-follow');

        currentX += (mouseX - currentX) * delay;
        currentY += (mouseY - currentY) * delay;

        if (cursor && follow) {
          cursor.style.left = `${mouseX}px`;
          cursor.style.top = `${mouseY}px`;
          follow.style.left = `${currentX}px`;
          follow.style.top = `${currentY}px`;
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Cursor setup
    if (isFinePointer) {
      window.addEventListener('mousemove', e => {
        mouseX = e.clientX;
        mouseY = e.clientY;
      });

      const interactive = document.querySelectorAll(
        'a, .passion-selector-bar button, .email-button, .project-inner-tile'
      );

      interactive.forEach(el => {
        el.addEventListener('mouseenter', () => {
          const cursor = document.getElementById('custom-cursor');
          const follow = document.getElementById('custom-cursor-follow');
          cursor.style.transform = 'translate(-50%, -50%) scale(0.5)';
          cursor.style.border = 'var(--white) 0px solid';
          cursor.style.backgroundColor = 'var(--white)';
          follow.style.border = '2px solid var(--white)';
          follow.style.opacity = '0';
        });
        el.addEventListener('mouseleave', () => {
          const cursor = document.getElementById('custom-cursor');
          const follow = document.getElementById('custom-cursor-follow');
          cursor.style.transform = 'translate(-50%, -50%) scale(1)';
          cursor.style.border = 'var(--highlight-1) 2px solid';
          cursor.style.backgroundColor = 'transparent';
          follow.style.border = '2px solid var(--highlight-1)';
          follow.style.opacity = '0.5';
        });
      });
    }


    let selectedId = null;
    let activeController = null;

    const updatePassionView = (id) => {
      const passionElements = document.querySelectorAll(".passion");
      const message = document.getElementById("passion-message");
      const desc = document.getElementById("passion-message-description");
      const tileBox = document.querySelector(".passion-tile-box");
      const isMobile = window.innerWidth <= 768;

      // On mobile, prevent deselecting (always keep one selected)
      if (selectedId === id && isMobile) {
        return;
      }

      if (selectedId === id && !isMobile) {
        // Reset view (desktop only) - show all three models
        selectedId = null;
        activeController = null;

        // Restart all three model controllers
        Object.values(controllers).forEach(controller => {
          if (controller && controller.start) {
            controller.start();
          }
        });

        document.querySelectorAll('.passion-selector-bar button')
        .forEach(b => b.classList.remove('active'));
        passionElements.forEach(el => {
          el.style.display = "flex";
          el.classList.remove("inactive", "selected");
        });
        message.classList.remove("visible");
        tileBox.classList.remove("message-view");
        return;
      } else {
        tileBox.classList.add("message-view");
      }

      // Fade out message first
      message.classList.remove("visible");

      selectedId = id;
      document.querySelectorAll('.passion-selector-bar button')
        .forEach(b => b.classList.remove('active'));
      document.querySelector(`.passion-selector-bar button[data-id="${id}"]`)
        ?.classList.add('active');

      const canvasId = id === 'coding' ? 'coding-model' : id === 'data' ? 'dna-model' : 'ml-model';

      const { description: d } = passions[id];

      passionElements.forEach(el => {
        const elId = el.getAttribute("data-id");
        if (elId !== id) {
          el.classList.add("inactive");
          if (isMobile) {
            // On mobile, use visibility to keep canvas rendered
            el.style.position = "absolute";
            el.style.visibility = "hidden";
            el.style.pointerEvents = "none";
          } else {
            // On desktop, hide completely after animation
            setTimeout(() => (el.style.display = "none"), 1100);
          }
        } else {
          if (isMobile) {
            // On mobile, show by removing absolute positioning
            el.style.position = "relative";
            el.style.visibility = "visible";
            el.style.pointerEvents = "auto";
            el.classList.remove("inactive");
          } else {
            // On desktop, normal fade in
            setTimeout(() => (el.style.display = "flex"), 1100);
            setTimeout(() => (el.classList.remove("inactive")), 1200);
          }
        }
      });

      // Pause all controllers, then start only the selected one
      Object.values(controllers).forEach(controller => {
        if (controller && controller.pause) {
          controller.pause();
        }
      });

      activeController = controllers[id];
      if (activeController) {
        activeController.start();
      }

      // Update description content and fade in with the model
      setTimeout(() => {
        desc.innerHTML = d;
        message.classList.add("visible");
        const c = document.getElementById(canvasId);
        // trigger a resize to refresh renderer + camera aspect
        if (c && typeof c.__threeResize === 'function') c.__threeResize();
      }, 1200);
    };

    document.querySelectorAll(".passion-selector-bar button").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.getAttribute("data-id");
        updatePassionView(id);
      });
    });

    // Auto-select Software (coding) on mobile, start all models on desktop
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      // On mobile: only show one model at a time
      setTimeout(() => updatePassionView('coding'), 0);
    } else {
      // On desktop: start all three models (default unselected state shows all three)
      Object.values(controllers).forEach(controller => {
        if (controller && controller.start) {
          controller.start();
        }
      });
    }


    // Scrolling behaviour
    const projectsSection = document.querySelector('.projects-section');
    const projectsContainer = projectsSection.querySelector('.projects-container');
    const isDesktop = window.innerWidth > 768;

    if (gsap && ScrollTrigger && isDesktop) {
      if (projectsSection) {
        gsap.to(projectsSection, {
          x: () => -(projectsContainer.scrollWidth - window.innerWidth),
          ease: 1,
          scrollTrigger: {
            trigger: projectsSection,
            pin: true,
            scrub: 1,
            markers: false,
            start: `bottom 91.67%`,
            end: `+=${projectsContainer.scrollWidth - window.innerWidth}`
          }
        });
      }
    }

    const stop = e => e.preventDefault();

    return () => {
      // Cancel animation frame
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      // Pause all model controllers
      Object.values(controllers).forEach(controller => {
        if (controller && controller.pause) {
          controller.pause();
        }
      });

      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      document.removeEventListener('gesturestart', stop);
      document.removeEventListener('gesturechange', stop);
      document.removeEventListener('gestureend', stop);
    };
  }, []); 

  return (
    <div className="App">
      <div id="custom-cursor" />
      <div id="custom-cursor-follow" />

      <div className="header-box">
        <div className="header-name-tag">Pascal Janek Pan Stümpfl</div>
        <div className="link-box">
          <a href="#passions-section-divider" className="overview-item">Passions</a>
          <a href="#projects-section" className="overview-item">Projects</a>
          <a href="#contact-section-divider" className="overview-item">Connect</a>
        </div>
      </div>

      <section className="introduction-section">
        <h1>Hi. I'm Pascal.<br></br>
        This is my <span className="glitch glitch-text" data-text="Portfolio">Portfolio</span>.</h1>
      </section>

      <section className="divider-section" id="passions-section-divider"></section>


      <section className="passions-section">
        <div className="passion-selector-bar">
          <button data-id="coding">Software</button>
          <button data-id="data">Data Analysis</button>
          <button data-id="ml">Machine Learning</button>
        </div>

        <div className="passion-tile-box">
        <div className="passion" data-id="coding">
          <canvas id="coding-model" className="model-canvas"></canvas>
        </div>
        <div className="passion" data-id="data">
          <canvas id="dna-model" className="model-canvas"></canvas>
        </div>
        <div className="passion" data-id="ml">
          <canvas id="ml-model" className="model-canvas"></canvas>
        </div>

        <div className="passion-message" id="passion-message">
          <p id="passion-message-description">Description</p>
        </div>
        </div>
      </section>


      <section className="divider-section" id="projects-section-divider"></section>

      <section className="projects-section" id="projects-section">
        <div className="projects-container">
          {projects.map(project => (
            <ProjectTile key={project.id} project={project} />
          ))}
        </div>
      </section>

     <section className="divider-section" id="contact-section-divider"></section>

      <section className="contact-section">
        <div className="contact-text">Let's get in touch.</div> {/* Previously: "Have a project or position in mind?" */}

        <a
          href="mailto:pascal.stuempfl@outlook.com?subject=I%20saw%20your%20projects%20and%20wanted%20to%20say%20hi%20%F0%9F%A4%9D"
          className="email-button"
          data-text="Contact me"
        >
          <span>Contact me</span>
        </a>

        <div className="contact-icons">
          <a
            href="https://linkedin.com/in/pascal-stuempfl"
            target="_blank"
            rel="noreferrer"
            aria-label="LinkedIn"
          >
            <i className="fa fa-linkedin" aria-hidden="true" />
            <span className="sr-only">LinkedIn</span>
          </a>

          <a
            href="https://github.com/PascalJPan"
            target="_blank"
            rel="noreferrer"
            aria-label="GitHub"
          >
            <i className="fa fa-github" aria-hidden="true" />
            <span className="sr-only">GitHub</span>
          </a>
        </div>

      </section>

      <section className="divider-section" id="end-section-divider"></section>

    </div>
  );
}

export default App;